import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import { FadeUp } from "@/components/Reveal";
import { PatchBadge, Spark } from "@/components/Doodles";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";

const AccountLogin = () => {
    const { login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        const res = await login(email, password);
        if (!res.ok) setError(res.error);
        setBusy(false);
    };

    return (
        <form onSubmit={submit} data-testid="account-login-form" className="mx-auto max-w-md border-2 border-ink bg-paper p-8 hard-shadow-lg -rotate-1">
            <Spark className="h-8 w-8 text-orange" />
            <h2 className="mt-2 font-display text-3xl tracking-wide text-ink">MEMBERS, SIGN IN</h2>
            <label className="mt-5 block text-sm font-semibold text-ink" htmlFor="account-email">Email</label>
            <input id="account-email" data-testid="account-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full border-2 border-ink bg-cream px-4 py-3 text-ink" />
            <label className="mt-4 block text-sm font-semibold text-ink" htmlFor="account-password">Password</label>
            <input id="account-password" data-testid="account-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full border-2 border-ink bg-cream px-4 py-3 text-ink" />
            {error && <p data-testid="account-login-error" className="mt-3 font-semibold text-brick">{error}</p>}
            <button data-testid="account-login-submit" type="submit" disabled={busy} className="btn-stamp mt-6 w-full disabled:opacity-60">
                {busy ? "One sec…" : "Sign In"}
            </button>
            <p className="mt-4 text-sm text-ink/60">
                No account yet? <Link to="/membership" className="font-semibold text-forest underline decoration-mustard decoration-2 underline-offset-4">Join the club</Link> — your login is created right after checkout.
            </p>
            <p className="mt-2 text-sm text-ink/60">
                <Link to="/reset-password" data-testid="account-forgot-link" className="font-semibold text-forest underline decoration-mustard decoration-2 underline-offset-4">Forgot your password?</Link>
            </p>
            <p className="mt-2 text-sm text-ink/60">
                Already paying dues but never set a password? <Link to="/activate" data-testid="account-activate-link" className="font-semibold text-forest underline decoration-mustard decoration-2 underline-offset-4">Activate your account</Link> — no paying twice.
            </p>
        </form>
    );
};

const AccountCard = ({ user, onLogout }) => {
    const [data, setData] = useState(null);
    const [events, setEvents] = useState([]);
    const [portalBusy, setPortalBusy] = useState(false);

    useEffect(() => {
        api.get("/account").then((r) => setData(r.data)).catch(() => setData({}));
        api.get("/events").then((r) => setEvents((r.data || []).slice(0, 3))).catch(() => {});
    }, []);

    const openPortal = async () => {
        setPortalBusy(true);
        try {
            const { data: d } = await api.post("/account/portal", { origin_url: window.location.origin });
            window.location.href = d.url;
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
            setPortalBusy(false);
        }
    };

    if (!data) return <p className="text-center font-hand text-2xl text-ink/60">Laminating your card…</p>;

    const hoursLeft = data.studio_hours_included ? Math.max(0, data.studio_hours_included - data.studio_hours_used) : 0;

    return (
        <div className="grid items-start gap-10 lg:grid-cols-[1.1fr_1fr]">
            <FadeUp>
                <div data-testid="membership-card" className="stamp-frame border-2 border-ink bg-forest p-8 text-cream hard-shadow-lg -rotate-1">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <p className="font-display text-sm tracking-[0.2em] text-mustard">OFFICIAL CLUBHOUSE MEMBER</p>
                            <h2 className="mt-2 font-display text-4xl tracking-wide">{data.tier_name ? data.tier_name.toUpperCase() : "NO TIER YET"}</h2>
                            {data.tier_price && <p className="font-display text-2xl text-mustard">{data.tier_price}</p>}
                        </div>
                        <PatchBadge className="h-20 w-20 shrink-0 rotate-6 text-orange" />
                    </div>
                    <div className="mt-6 space-y-2 border-t-2 border-dashed border-cream/30 pt-5 text-sm">
                        <p><span className="text-cream/60">Name:</span> {data.name}</p>
                        <p><span className="text-cream/60">Email:</span> {data.email}</p>
                        {data.member_since && (
                            <p><span className="text-cream/60">Member since:</span> {new Date(data.member_since).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>
                        )}
                        {data.next_billing_date && (
                            <p data-testid="account-next-billing">
                                <span className="text-cream/60">{data.cancel_at_period_end ? "Membership ends:" : "Next dues:"}</span>{" "}
                                {new Date(data.next_billing_date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                                {data.cancel_at_period_end ? " (cancellation scheduled)" : ""}
                            </p>
                        )}
                        {data.membership_status === "past_due" && (
                            <p data-testid="account-status-warning" className="text-orange">
                                <span className="text-cream/60">Status:</span> payment hiccup — update your card via Manage Membership below and we'll pretend it never happened.
                            </p>
                        )}
                        <p><span className="text-cream/60">Upcoming work room reservations:</span> {data.upcoming_bookings}</p>
                    </div>
                    {data.studio_hours_included > 0 && (
                        <div className="mt-5">
                            <p className="font-display text-sm tracking-[0.15em] text-mustard">
                                INCLUDED WORK ROOM HOURS — {data.studio_hours_used} USED / {hoursLeft} LEFT
                            </p>
                            <div className="mt-2 h-5 border-2 border-cream/60 bg-ink/40">
                                <div
                                    data-testid="studio-hours-bar"
                                    className="h-full bg-mustard"
                                    style={{ width: `${Math.min(100, (data.studio_hours_used / data.studio_hours_included) * 100)}%` }}
                                />
                            </div>
                            <p className="mt-1 text-xs text-cream/60">Resets monthly. Doesn't roll over. Use it or lose it (lovingly).</p>
                        </div>
                    )}
                    {(data.studio_hours_history || []).length > 0 && (
                        <div data-testid="studio-hours-history" className="mt-5 border-t-2 border-dashed border-cream/30 pt-4">
                            <p className="font-display text-sm tracking-[0.15em] text-mustard">WORK ROOM HOURS LOG</p>
                            <ul className="mt-2 space-y-1 text-sm">
                                {data.studio_hours_history.map((h) => (
                                    <li key={h.month} className="flex justify-between gap-4">
                                        <span>{new Date(`${h.month}-02`).toLocaleDateString("en-US", { month: "long", year: "numeric" })}</span>
                                        <span className="font-bold">{h.hours} hr{h.hours === 1 ? "" : "s"}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </FadeUp>
            <FadeUp delay={0.12} className="space-y-6">
                {!data.membership_tier && (
                    <div className="border-2 border-dashed border-ink/50 bg-cream p-6">
                        <h3 className="font-display text-xl tracking-wide text-ink">NO ACTIVE MEMBERSHIP</h3>
                        <p className="mt-2 text-sm text-ink/70">Pick a tier to unlock member perks — every paid tier includes free shared Work Room access.</p>
                        <Link to="/membership" data-testid="account-join-link" className="btn-stamp mt-4 !px-5 !py-2 !text-base">See Memberships</Link>
                    </div>
                )}
                <div className="border-2 border-ink bg-paper p-6 hard-shadow">
                    <h3 className="font-display text-xl tracking-wide text-ink">COMING UP AT THE CLUBHOUSE</h3>
                    {events.length === 0 ? (
                        <p className="mt-3 text-sm text-ink/60">Nothing pinned up just yet. Check back soon.</p>
                    ) : (
                        <ul className="mt-3 space-y-2">
                            {events.map((e) => (
                                <li key={e.id} className="flex items-baseline justify-between gap-3 border-b border-dotted border-ink/25 pb-2 text-sm">
                                    <span className="font-semibold text-ink">{e.title}</span>
                                    <span className="shrink-0 text-ink/60">{e.date}{e.time ? ` · ${e.time}` : ""}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    <Link to="/whats-happening" data-testid="account-events-link" className="mt-3 inline-block font-display text-sm tracking-wider text-forest underline decoration-mustard decoration-2 underline-offset-4 hover:text-brick">
                        The whole board →
                    </Link>
                </div>
                <div className="border-2 border-ink bg-paper p-6 hard-shadow">
                    <h3 className="font-display text-xl tracking-wide text-ink">QUICK MOVES</h3>
                    <div className="mt-4 flex flex-col gap-3">
                        <Link to="/work-room" data-testid="account-studio-link" className="btn-stamp !bg-orange">Reserve the Work Room</Link>
                        {data.has_billing ? (
                            <button onClick={openPortal} disabled={portalBusy} data-testid="account-portal-button" className="btn-stamp !bg-brick disabled:opacity-60">
                                {portalBusy ? "Opening the ledger…" : "Manage or Cancel Membership"}
                            </button>
                        ) : (
                            <p className="text-sm text-ink/60">Billing questions? Email panthercityclubhouse@gmail.com and a human will handle it.</p>
                        )}
                        <button onClick={onLogout} data-testid="account-logout-button" className="border-2 border-ink bg-cream px-5 py-3 font-display text-lg tracking-wider text-ink hard-shadow">
                            Sign Out
                        </button>
                    </div>
                </div>
            </FadeUp>
        </div>
    );
};

const Account = () => {
    const { user, logout } = useAuth();
    return (
        <>
            <Seo title="My Account" description="Your Panther City Clubhouse membership card, work room hours, and billing." />
            <header className="mx-auto max-w-6xl px-5 pb-10 pt-32 sm:px-10 sm:pt-40">
                <FadeUp>
                    <p className="inline-block -rotate-2 font-hand text-2xl text-orange sm:text-3xl">your corner of the clubhouse</p>
                    <h1 className="mt-2 font-display text-5xl leading-[0.95] tracking-wide text-ink sm:text-6xl">MY CLUBHOUSE CARD</h1>
                </FadeUp>
            </header>
            <div className="mx-auto max-w-6xl px-5 pb-20 sm:px-10">
                {user === null ? (
                    <p className="text-center font-hand text-2xl text-ink/60">Checking the member list…</p>
                ) : user ? (
                    <AccountCard user={user} onLogout={logout} />
                ) : (
                    <AccountLogin />
                )}
            </div>
            <Marquee />
        </>
    );
};

export default Account;
